import {
  Button,
  ButtonSize,
  ButtonVariant
} from '@amzn/stencil-react-components/button';
import { StencilProvider } from '@amzn/stencil-react-components/context';
import { FileUpload } from '@amzn/stencil-react-components/file-upload';
import {
  IconCross,
  IconDownload,
  IconTick
} from '@amzn/stencil-react-components/icons';
import { Flex } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType
} from '@amzn/stencil-react-components/message-banner';
import { Text } from '@amzn/stencil-react-components/text';
import Papa from 'papaparse';
import React, { useState } from 'react';
import EntityTable from 'src/components/EntityTable';
import { BulkFileUploadExpectedHeaders } from 'src/configurations/InputElementConfigs';
import { DEFAULT_TEMPLATE_FILE_NAME } from 'src/constants/DisplayConstants';
import { BulkActionTypeToCSVHeadersTypeMap } from 'src/constants/ServiceConstants';
import { EntityType } from 'src/constants/enums';
import { MCBulkEmailSchema } from 'src/transformers/schema/bulkEmailSchema';
import { FileUtils } from 'src/utils/FileUtils';

export enum BulkActionCaseRelatedEntitiesType {
  CREATE_CASE = 'CREATE_CASE',
  CREATE_CASE_V2 = 'CREATE-V2_CASE',
  CREATE_ASSET_V2 = 'CREATE-V2_CORRESPONDENCE', // TODO: Update to CREATE-V2_ASSET when the bulk API supports it
  SEND_EMAILS = 'SEND_EMAILS',
  UPDATE_CASE = 'UPDATE_CASE',
  UPDATE_CASE_V2 = 'UPDATE-V2_CASE',
  UPDATE_ASSET_V2 = 'UPDATE-V2_CORRESPONDENCE', // TODO: Update to UPDATE-V2_ASSET when the bulk API supports it
  DELETE_CASE = 'DELETE_CASE',
  GET_LEGAL_REPORT = 'GET_LEGAL_REPORT',
  DEFAULT = 'DEFAULT'
}

interface BulkActionPageProps {
  bulkActionName: string;
  domainId: string;
  entityType: EntityType;
  templateFileName?: string;
  onClose?: () => void;
  onSave?: (file: File, actionType: BulkActionCaseRelatedEntitiesType) => void;
  bulkActionType: BulkActionCaseRelatedEntitiesType;
  enableActionColumn?: boolean;
  enableRowSelection?: boolean;
}

const downloadUploadFileTemplate = (
  entityType: EntityType,
  bulkActionType: BulkActionCaseRelatedEntitiesType,
  templateFileName?: string
) => {
  const headers = BulkActionTypeToCSVHeadersTypeMap.get(bulkActionType)
    ? BulkActionTypeToCSVHeadersTypeMap.get(bulkActionType)
    : BulkFileUploadExpectedHeaders[entityType];
  const fileName = templateFileName ?? DEFAULT_TEMPLATE_FILE_NAME;
  FileUtils.downloadCsvWithHeader({ headers, fileName });
};

export const BulkActionCaseRelatedEntitiesPage = ({
  onClose,
  onSave,
  bulkActionName,
  templateFileName,
  entityType,
  domainId,
  bulkActionType,
  enableActionColumn = true,
  enableRowSelection = true
}: BulkActionPageProps) => {
  const [file, setFile] = useState<File>(
    new File([''], 'dummy.csv', { type: 'text/csv' })
  );
  const [validationError, setValidationError] = useState<string | undefined>();
  const [tableEntries, setTableEntries] = useState<Array<unknown>>([]);
  const [requiredFields, setRequiredFields] = useState([]);

  const parseObjectData = (results): void => {
    setValidationError(undefined);
    const newRecordList: any = [];
    if (results.data.length === 0) {
      setValidationError(
        'No rows found in the CSV file. Please upload CSV with non-zero rows'
      );
    }
    results.data.forEach((record) => {
      try {
        for (const field in record) {
          record[field] = record[field].trim();
          if (record[field] === '') {
            delete record[field];
          }
        }
        newRecordList.push(record);
      } catch (error) {
        console.error(error.message);
      }
    });

    // TODO: Move this to a generic validator and check across all domains
    if (
      domainId === 'MC' &&
      bulkActionType === BulkActionCaseRelatedEntitiesType.SEND_EMAILS
    ) {
      const result = MCBulkEmailSchema.safeParse(newRecordList);
      if (result.success) {
        setTableEntries(result.data as Array<unknown>);
      } else {
        setValidationError(JSON.stringify(result.error.format()));
        return;
      }
    }
    setTableEntries(newRecordList);
  };

  const fileUploadHandler = (files) => {
    if (files.length > 0) {
      Papa.parse(files[0], {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const headers = results.meta.fields;
          const newRequiredFields = headers.map((header) => ({
            header,
            accessor: header
          }));
          setRequiredFields(newRequiredFields);
          parseObjectData(results);
          setFile(files[0]);
        }
      });
    } else {
      setTableEntries([]);
    }
  };

  return (
    <StencilProvider>
      {validationError && (
        <MessageBanner isDismissible type={MessageBannerType.Error}>
          {validationError}
        </MessageBanner>
      )}
      <Flex flexDirection={'row'} justifyContent={'space-between'}>
        <Flex
          flexDirection={'row'}
          justifyContent={'flex-start'}
          columnGap={'10px'}
          margin={'10px'}
        >
          <Text textAlign="center" fontWeight={500}>
            {bulkActionName}
          </Text>
        </Flex>
        <Flex
          flexDirection={'row'}
          columnGap={'10px'}
          justifyContent={'flex-end'}
          alignItems={'center'}
        >
          <Button
            icon={<IconDownload />}
            variant={ButtonVariant.Primary}
            size={ButtonSize.Small}
            onClick={() =>
              downloadUploadFileTemplate(
                entityType,
                bulkActionType,
                templateFileName
              )
            }
          >
            Download sample csv file
          </Button>
          <Button
            icon={<IconTick />}
            size={ButtonSize.Small}
            disabled={
              validationError !== undefined || tableEntries.length === 0
            }
            onClick={() => (onSave ? onSave(file, bulkActionType) : null)}
          >
            Submit
          </Button>
          <Button
            icon={<IconCross />}
            isDestructive={true}
            size={ButtonSize.Small}
            onClick={() => (onClose ? onClose() : null)}
          >
            Close
          </Button>
        </Flex>
      </Flex>

      <Flex flexDirection={'column'} margin={'10px'}>
        <FileUpload
          accept=".csv"
          maxFiles={1}
          onFileAttached={(files) => {
            fileUploadHandler(files);
          }}
        />
      </Flex>
      {tableEntries.length > 0 ? (
        <EntityTable
          data={tableEntries}
          domainId={domainId}
          requiredFields={requiredFields}
          enableActionColumn={enableActionColumn}
          enableRowSelection={enableRowSelection}
        />
      ) : null}
    </StencilProvider>
  );
};
